// src/pages/SettingsPage.js

/**
 * SettingsPage – lets the user manage account and support settings.
 * 
 * Features:
 * - Logout logic (`handleLogout`):
 *     • Signs out via Firebase Auth.
 *     • Clears localStorage.
 *     • Redirects to `/login` via React Router.
 * - Customer support ticket submission (`handleSubmitTicket`):
 *     • Builds a unique ticket ID.
 *     • Gathers user email from AuthContext.
 *     • Posts ticket data to `/api/submit-ticket`.
 *     • Alerts on success or failure.
 * - Consent toggle (`handleToggleConsent`):
 *     • Toggles and persists data collection consent.
 * - Subscription management:
 *     • Shows current subscription status.
 *     • "Subscribe Now" button for non-subscribers.
 * 
 * State:
 * - `consent` (boolean) for data‑usage opt‑in.
 * - `supportText` (string) for support message.
 * - `subscriptionStatus` (object) for subscription info.
 * 
 * Contexts & Hooks:
 * - AuthContext.currentUser for userEmail.
 * - useNavigate for programmatic routing.
 * 
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

/**
 * ## TODOs:
 * ## - Set up a mailer for support tickets
 */

import React, { useState, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Firebase
import { signOut } from 'firebase/auth'
import { auth } from '../firebaseConfig'

// Context
import { AuthContext } from '../context/AuthContext'

// Components
import Navbar from './components/Navbar'
import PaywallModal from './components/PaywallModal'

// Local CSS
import styles from './SettingsPage.module.css'

// Authentication helpers
import { authenticatedFetch } from '../utils/authHelpers'

function SettingsPage() {
  // Initialize consent from localStorage or default to true
  const [consent, setConsent] = useState(() => {
    const savedConsent = localStorage.getItem('dataCollectionConsent');
    return savedConsent !== null ? JSON.parse(savedConsent) : true;
  });

  // Subscription State
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  //** Logout Logic **//
  const handleLogout = async () => {
    try {
      // Sign out the user
      await signOut(auth);
      // Clear local storage
      localStorage.removeItem('currentUser');
      // Redirect to the login page
      navigate('/login');

    } catch (error) {
      console.error('Error logging out:', error);
      alert('Error logging out. Please try again.');
    }
  };

  //** Navigate to Subscribe **//
  const handleSubscribeNow = () => {
    navigate('/subscribe');
  };

  //** Subscription Management Actions **//
  const handleCancelSubscription = async () => {
    // Show confirmation dialog
    const confirmCancel = window.confirm(
      'Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period.'
    );
    
    if (!confirmCancel) return;

    try {
      const response = await authenticatedFetch('/api/stripe/cancel-subscription', {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        // Show simplified success message
        alert('Subscription canceled successfully. You will keep uploading access until the end of your current billing period.');
        
        // Refresh subscription details
        fetchSubscriptionDetails();
      } else {
        throw new Error(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert(`Error canceling subscription: ${error.message}`);
    }
  };

  const handleReactivateSubscription = async () => {
    // Check if user is in pending cancellation or fully canceled
    if (subscriptionDetails.status === 'canceled') {
      // Fully canceled - need new subscription
      navigate('/subscribe');
    } else if (subscriptionDetails.cancelAtPeriodEnd) {
      // Pending cancellation - just toggle the flag
      await handleToggleCancellation();
    }
  };

  const handleToggleCancellation = async () => {
    try {
      const response = await authenticatedFetch('/api/stripe/toggle-cancellation', {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        alert('Cancellation removed successfully. Your subscription will continue.');
        
        // Refresh subscription details
        fetchSubscriptionDetails();
      } else {
        throw new Error(data.error || 'Failed to remove cancellation');
      }
    } catch (error) {
      console.error('Error removing cancellation:', error);
      alert(`Error removing cancellation: ${error.message}`);
    }
  };

  const handleRevertPlanChange = async () => {
    try {
      const response = await authenticatedFetch('/api/stripe/revert-plan-change', {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        alert('Pending plan change has been reverted.');
        fetchSubscriptionDetails();
      } else {
        throw new Error(data.error || 'Failed to revert plan change');
      }
    } catch (error) {
      console.error('Error reverting plan change:', error);
      alert(`Error reverting plan change: ${error.message}`);
    }
  };

  const handleChangePlan = () => {
    navigate('/subscribe?changePlan=1');
  };

  // Fetch detailed subscription information for dashboard
  const fetchSubscriptionDetails = async () => {
    try {
      setLoadingSubscription(true);
      const response = await authenticatedFetch('/api/user/subscription-details');
      
      if (response.ok) {
        const details = await response.json();
        setSubscriptionDetails(details);
      } else {
        console.error('Failed to fetch subscription details');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  //** Consent Toggle Logic **//
  const handleToggleConsent = () => {
    const newConsent = !consent;
    setConsent(newConsent);
    localStorage.setItem('dataCollectionConsent', JSON.stringify(newConsent));
  }

  //## ======================= SUBSCRIPTION CHECK ======================= ##//
  
  // Check subscription status on page load
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await authenticatedFetch('/api/user/subscription-status');
        
        if (response.ok) {
          const { subscription } = await response.json();
          setShowPaywallModal(subscription.status === 'inactive' || !subscription.status);
        } else {
          // If we can't verify subscription, show modal for safety
          setShowPaywallModal(true);
        }
      } catch (error) {
        console.error('Subscription check failed:', error);
        // Show modal if subscription check fails
        setShowPaywallModal(true);
      }
    };

    checkSubscription();
  }, []);

  // Fetch detailed subscription information on component mount
  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  return (
    <main className={styles.settingsPage}>

      {/* Navbar */}
      <Navbar />

      {/* Main Content */}
      <div className={styles.settingsContainer}>
        
        {/* Subscription Dashboard */}
        {!loadingSubscription && subscriptionDetails && (
          <div className={styles.subscriptionDashboard}>
            <h3>Subscription Management</h3>
            
            <div className={styles.dashboardContainer}>
              {/* Left Column - Info Boxes (80%) */}
              <div className={styles.infoColumn}>
                <div className={styles.infoGrid}>
                  {/* Current Tier */}
                  <div className={styles.infoBox}>
                    <div className={styles.infoLabel}>Current Tier</div>
                    <div className={styles.infoValue}>
                      {subscriptionDetails.tier ? subscriptionDetails.tier.toUpperCase() : 'None'}
                      {subscriptionDetails.pendingTier && (
                        <span style={{ marginLeft: '8px', fontSize: '0.9rem', color: '#888' }}>
                          (Changing next cycle → {subscriptionDetails.pendingTier.toUpperCase()})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Documents Used */}
                  <div className={styles.infoBox}>
                    <div className={styles.infoLabel}>Documents Used</div>
                    <div className={styles.infoValue}>
                      {subscriptionDetails.documentsUsed || 0}/{subscriptionDetails.documentsLimit || '∞'}
                    </div>
                  </div>

                  {/* End of Cycle */}
                  <div className={styles.infoBoxWide}>
                    <div className={styles.infoLabel}>End of Cycle</div>
                    <div className={styles.infoValue}>
                      {subscriptionDetails.currentPeriodEnd 
                        ? new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'
                      }
                    </div>
                  </div>

                  {/* Status */}
                  <div className={styles.infoBox}>
                    <div className={styles.infoLabel}>Status</div>
                    <div className={`${styles.infoValue} ${styles.statusIndicator}`}>
                      {subscriptionDetails.status === 'active' && !subscriptionDetails.cancelAtPeriodEnd && '🟢 Active'}
                      {subscriptionDetails.status === 'active' && subscriptionDetails.cancelAtPeriodEnd && '🟡 Active (Cancelling)'}
                      {subscriptionDetails.status === 'canceled' && '🔴 Canceled'}
                      {subscriptionDetails.status === 'past_due' && '🟡 Past Due'}
                      {subscriptionDetails.status === 'inactive' && '⚪ Inactive'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Vertical Separator */}
              <div className={styles.verticalDivider}></div>

              {/* Right Column - Action Buttons (15%) */}
              <div className={styles.actionsColumn}>
                <div className={styles.actionButtons}>
                  <button 
                    className={`${styles.actionButton} ${(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? '' : styles.actionButtonDisabled}`}
                    onClick={(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? handleReactivateSubscription : undefined}
                    disabled={!(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd)}
                  >
                    {subscriptionDetails.status === 'canceled' ? 'Reactivate' : 'Keep Subscription'}
                  </button>
                  
                  <button 
                    className={`${styles.actionButton} ${(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? styles.actionButtonDisabled : ''}`}
                    onClick={!(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? handleCancelSubscription : undefined}
                    disabled={subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd}
                  >
                    Cancel
                  </button>
                  
                  {subscriptionDetails.pendingTier ? (
                    <button
                      className={styles.actionButton}
                      onClick={handleRevertPlanChange}
                    >
                      Revert Plan
                    </button>
                  ) : (
                    <button 
                      className={`${styles.actionButton} ${(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? styles.actionButtonDisabled : ''}`}
                      onClick={!(subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd) ? handleChangePlan : undefined}
                      disabled={subscriptionDetails.status === 'canceled' || subscriptionDetails.cancelAtPeriodEnd}
                    >
                      Change Plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logout Button */}
        <button className={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>

        {/* Customer Support Section commented out until email system is ready */}
        {/* <div className={styles.supportSection}>
          <h3>Customer Support</h3>
          <textarea
            className={styles.supportTextArea}
            placeholder="Let us know what's wrong!"
            value={supportText}
            onChange={(e) => setSupportText(e.target.value)}
          />
          <button className={styles.submitButton} onClick={handleSubmitTicket}>
            Submit Ticket
          </button>
        </div> */}

        {/* Consent / Data usage Section */}
        <div className={styles.consentSection}>
          <p className={styles.consentText}>
            We collect the files uploaded to further train our AI model to enhance 
            your experience and quality of service. However, you are always able to opt-out. 
            I consent and give permission to Gigemai to collect and use the data of relevance.
          </p>
          <div className={styles.consentToggleRow}>
            <div className={styles.toggleContainer}>
              <input
                type="checkbox"
                checked={consent}
                onChange={handleToggleConsent}
                className={styles.toggleCheckbox}
                id="consentToggle"
              />
              <label htmlFor="consentToggle" className={styles.toggleSlider}></label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.footerLeft}>Gigemai</p>
          <div className={styles.footerRight}>
            <p>Contact Support: </p>
            <p>support@gigemai.com</p>
          </div>
        </div>
        
      </div>
      
      {/* Paywall Modal - Shows for unsubscribed users */}
      {showPaywallModal && <PaywallModal />}
    </main>
  )
}

export default SettingsPage